import { studyStatesOf } from './card-utils';
import type { ProgressIndicatorsData, SpacedRepetitionCard } from './types';

/**
 * Indicator types rendered alongside an active study session.
 * - SR sessions show `todo`, `learning`, `done`
 * - Fast-review sessions show `todo`, `fastReviewDone`
 *
 * Web and mobile share these constants so a future label rename only
 * needs to ship through the shared package.
 */
export const PROGRESS_INDICATOR_TYPES = [
  'todo',
  'learning',
  'done',
  'fastReviewDone',
] as const;

export type ProgressIndicatorType = (typeof PROGRESS_INDICATOR_TYPES)[number];

export const PROGRESS_INDICATOR_LABEL_KEYS = {
  todo: 'flashcardsSlider.statuses.todo',
  learning: 'flashcardsSlider.statuses.learning',
  done: 'flashcardsSlider.statuses.done',
  fastReviewDone: 'flashcardsSlider.statuses.done',
} as const satisfies Record<ProgressIndicatorType, string>;

export const PROGRESS_INDICATOR_TOOLTIP_KEYS = {
  todo: 'flashcardsSlider.tooltips.cardsfLeft',
  learning: 'flashcardsSlider.tooltips.learning',
  done: 'flashcardsSlider.tooltips.cardsCompleted',
  fastReviewDone: 'flashcardsSlider.tooltips.cardsCompleted',
} as const satisfies Record<ProgressIndicatorType, string>;

export function getProgressIndicatorOrder(
  isFastReview: boolean,
): ProgressIndicatorType[] {
  return isFastReview ? ['todo', 'fastReviewDone'] : ['todo', 'learning', 'done'];
}

export function getProgressIndicatorValue(
  type: ProgressIndicatorType,
  data: ProgressIndicatorsData,
  isFastReview: boolean,
): number {
  switch (type) {
    case 'todo':
      return isFastReview
        ? data.cardsTodoInFastReviewSession
        : data.cardsTodoInSpacedRepetitionSession;
    case 'learning':
      return data.cardsLearningThisSession;
    case 'done':
    case 'fastReviewDone':
      return data.cardsStudiedThisSession;
  }
}

/**
 * Compute the four session counts shown in `ProgressIndicators` from the
 * current card queue. Splits the queue into "todo" vs "learning" based on
 * whether each card has been graded since `sessionStartISO`.
 *
 * Returns indicator counts only — for the full `ProgressData` (incl. the
 * progress bar's step counts), call `composeProgressData` in scheduling.ts.
 */
export function composeProgressIndicatorsData<TCard extends SpacedRepetitionCard>(
  cardQueue: TCard[],
  cardsStudiedThisSession: number,
  sessionStartISO: string,
): ProgressIndicatorsData {
  const sessionStartMs = new Date(sessionStartISO).getTime();

  const cardsLearningThisSession = cardQueue.filter((card) => {
    // A row with no history at all is a new card (Sentry FLASHKA-APP-JV).
    const states = studyStatesOf(card);
    const lastStudyState = states[states.length - 1];
    if (!lastStudyState) return false;
    return new Date(lastStudyState.ISO).getTime() >= sessionStartMs;
  }).length;

  return {
    cardsStudiedThisSession,
    cardsTodoInFastReviewSession: cardQueue.length,
    cardsTodoInSpacedRepetitionSession: cardQueue.length - cardsLearningThisSession,
    cardsLearningThisSession,
  };
}
